import { paymentAttentionReason } from '../scorecard/paymentAttention';
import { PaymentAttentionOrders } from './PaymentAttentionOrders';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { operationalQueryPolicy, safePagePlaceholder } from '../../api/queryPolicies';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getShipments } from '../../api/endpoints/admin';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { ShipmentStatus } from '../../api/types';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { shipmentStatusLabel } from './shipmentStatusLabel';
import { StatusPill } from '../../components/ui/StatusPill';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { shipmentStatusColor } from '../../components/domain/statusColor';
import { ShipmentFormModal } from './ShipmentFormModal';
import { curr, dualPrice, partnerPriceLabel } from '../../utils/price';
import { formatDistance, formatCompactDuration } from '../../utils/distance';
import { OwnerChip } from '../ownership/OwnerChip';
import { opsUserName, useOpsUser, useOpsUsers } from '../ownership/opsUser';
import { ownerFilterParam, type OwnerFilter } from './ownerFilter';
import { ApproxMarker } from './ApproxMarker';
import { VehicleCell } from '../../components/domain/VehicleCell';
import styles from './ShipmentsView.module.css';
import { RouteText } from '../../components/domain/RouteText';
import { StoppedPill } from './StoppedPill';

/** 'STOPPED' = has an open stop (dnm-work#165 C5: `stopped=true`, replaces the EXCEPTION status filter). */
type StatusFilter = Exclude<ShipmentStatus, 'EXCEPTION'> | 'ACTIVE' | 'STOPPED' | '';

const STATUS_OPTS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Stopped', value: 'STOPPED' },
  { label: 'Delivered', value: 'DELIVERED' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Expired', value: 'EXPIRED' },
  { label: 'Posted', value: 'POSTED' },
  { label: 'Draft', value: 'DRAFT' },
];

function fmt(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function ShipmentsView() {
  const location = useLocation();
  const raw = new URLSearchParams(location.search).get('shipmentIds');
  if (raw !== null) {
    const ids = [
      ...new Set(
        raw
          .split(',')
          .filter((value) => /^\d+$/.test(value))
          .map(Number)
          .filter((id) => Number.isSafeInteger(id) && id > 0),
      ),
    ].slice(0, 50);
    return (
      <PaymentAttentionOrders
        ids={ids}
        reason={paymentAttentionReason(new URLSearchParams(location.search).get('attention'))}
      />
    );
  }
  return <ShipmentList />;
}

function ShipmentList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const stateRestore = location.state as {
    page?: number;
    search?: string;
    status?: StatusFilter;
    owner?: OwnerFilter;
  } | null;

  const initialPage = searchParams.get('page')
    ? Number(searchParams.get('page'))
    : (stateRestore?.page ?? 0);
  const initialSearch = searchParams.get('search') ?? stateRestore?.search ?? '';
  // An old bookmark may still say ?status=EXCEPTION: that view is now the Stopped filter.
  const urlStatus = searchParams.get('status');
  const initialStatus =
    (urlStatus === 'EXCEPTION' ? 'STOPPED' : (urlStatus as StatusFilter | null)) ??
    stateRestore?.status ??
    'ACTIVE';

  const [page, setPage] = useState(!Number.isNaN(initialPage) ? initialPage : 0);
  const [search, setSearch] = useState(initialSearch);
  const debouncedSearch = useDebouncedValue(search, 300, () => setPage(0));
  // Default to the Active filter — ops care about in-flight shipments first
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [owner, setOwner] = useState<OwnerFilter>(
    searchParams.get('owner') ?? stateRestore?.owner ?? '',
  );
  const { opsUser } = useOpsUser();
  const opsUsers = useOpsUsers();

  function handleSearch(v: string) {
    setSearch(v);
  }

  const buildReturnUrl = () => {
    const params = new URLSearchParams();
    if (page > 0) params.set('page', String(page));
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (status && status !== 'ACTIVE') params.set('status', status);
    if (owner) params.set('owner', owner);
    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  };

  const navState = {
    from: buildReturnUrl(),
    fromLabel: 'Shipments',
    restoreState: { page, search: debouncedSearch, status, owner },
  };

  const queryKey = ['admin', 'shipments', page, debouncedSearch, status, owner] as const;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      getShipments({
        page,
        size: 25,
        search: debouncedSearch || undefined,
        status: status && status !== 'STOPPED' ? status : undefined,
        ...(status === 'STOPPED' ? { stopped: true } : {}),
        owner: ownerFilterParam(owner),
      }),
    ...operationalQueryPolicy,
    placeholderData: safePagePlaceholder(queryKey, 2),
  });

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <input
          id="shipments-search"
          className={styles.search}
          type="search"
          placeholder={t('shipments.shipmentsView.searchPlaceholder')}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <div className={styles.filters}>
          {STATUS_OPTS.map((opt) => (
            <button
              key={opt.value}
              className={status === opt.value ? `${styles.chip} ${styles.chipActive}` : styles.chip}
              onClick={() => {
                setStatus(opt.value as StatusFilter);
                setPage(0);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <select
          aria-label={t('shipments.shipmentsView.ownerLabel')}
          className={styles.ownerSelect}
          value={owner}
          onChange={(e) => {
            setOwner(e.target.value);
            setPage(0);
          }}
        >
          <option value="">{t('shipments.shipmentsView.ownerAll')}</option>
          <option value="me">{t('shipments.shipmentsView.ownerMe')}</option>
          <option value="unassigned">{t('shipments.shipmentsView.ownerUnassigned')}</option>
          {(opsUsers.data ?? [])
            .filter((u) => u.id !== opsUser?.id)
            .map((u) => (
              <option key={u.id} value={String(u.id)}>
                {t('shipments.shipmentsView.ownerUser', { name: opsUserName(u) })}
              </option>
            ))}
        </select>
        <RefreshButton
          queryKeys={[
            ['admin', 'shipments'],
            ['admin', 'dashboard'],
          ]}
        />
        <button className={styles.newBtn} onClick={() => setIsNewOpen(true)}>
          {t('shipments.shipmentsView.newShipment')}
        </button>
      </div>

      <DataTable
        loading={isLoading}
        error={isError ? 'We couldn’t load shipments' : undefined}
        onRetry={() => refetch()}
        keyFn={(s) => s.id}
        rows={data?.items ?? []}
        onRowClick={(s) => navigate(`/shipments/${s.id}`, { state: navState })}
        empty="No shipments match the current filter"
        columns={[
          {
            key: 'id',
            header: 'ID',
            width: '64px',
            render: (s) => (
              <Link to={`/shipments/${s.id}`} state={navState} className={styles.detailLink}>
                #{s.id}
              </Link>
            ),
          },
          {
            key: 'route',
            header: 'Route',
            render: (s) => (
              <Link to={`/shipments/${s.id}`} state={navState} className={styles.detailLink}>
                <RouteText route={s} className={styles.routeLine} />
                {formatDistance(s.distanceKm) && (
                  <div className={styles.routeMeta}>
                    {formatDistance(s.distanceKm)}
                    <ApproxMarker estimated={s.distanceIsEstimated} />
                    {formatCompactDuration(s.estimatedDurationMinutes) && (
                      <span> ({formatCompactDuration(s.estimatedDurationMinutes)})</span>
                    )}
                  </div>
                )}
              </Link>
            ),
          },
          {
            key: 'vehicle',
            header: 'Vehicle',
            width: '140px',
            render: (s) => <VehicleCell assignment={s.assignment} />,
          },
          { key: 'pickup', header: 'Pickup', width: '110px', render: (s) => fmt(s.pickupDate) },
          {
            key: 'delivery',
            header: 'Delivery',
            width: '110px',
            render: (s) => fmt(s.deliveryDate),
          },
          {
            key: 'price',
            header: 'Price',
            width: '110px',
            // Original prominent; margin-adjusted view beneath in muted text
            render: (s) => {
              // #288, R36: before award the partner price (or "—"), never a margin-derived carrier figure.
              if (s.finalPrice == null) {
                return (
                  <span>
                    {curr(s.offeredPrice)}
                    <span className={styles.priceSub}>{partnerPriceLabel(s.partnerOfferPrice)}</span>
                  </span>
                );
              }
              const dual = dualPrice(s.finalPrice, s.shipperFacingFinalPrice);
              return (
                <span>
                  {dual.original}
                  {dual.adjusted && <span className={styles.priceSub}>{t('shipments.shipmentsView.withMargin', { amount: dual.adjusted })}</span>}
                </span>
              );
            },
          },
          {
            key: 'owner',
            header: 'Owner',
            width: '170px',
            render: (s) => <OwnerChip owner={s.owner} meId={opsUser?.id} />,
          },
          {
            key: 'status',
            header: 'Status',
            width: '140px',
            render: (s) => {
              const c = shipmentStatusColor(s.status);
              return (
                <span className={styles.statusCell}>
                  <StatusPill bg={c.bg} text={c.text} label={shipmentStatusLabel(s.status)} title={s.status} />
                  {s.openStop && <StoppedPill />}
                </span>
              );
            },
          },
        ]}
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          size={data.size}
          onPage={setPage}
        />
      )}

      {isNewOpen && (
        <ShipmentFormModal
          onClose={() => setIsNewOpen(false)}
          onSaved={(created) => navigate(`/shipments/${created.id}`, { state: navState })}
        />
      )}
    </div>
  );
}
